import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { useInView } from 'motion/react'
import { annotate } from 'rough-notation'
import type { RoughAnnotation } from 'rough-notation/lib/model'

type AnnotationAction =
  | 'highlight'
  | 'underline'
  | 'box'
  | 'circle'
  | 'strike-through'
  | 'crossed-off'
  | 'bracket'

interface HighlighterProps {
  children: ReactNode
  action?: AnnotationAction
  color?: string
  strokeWidth?: number
  animationDuration?: number
  iterations?: number
  padding?: number
  multiline?: boolean
}

export const Highlighter = ({
  children,
  action = 'highlight',
  color = '#ffb347',
  strokeWidth = 1.5,
  animationDuration = 1200,
  iterations = 2,
  padding = 2,
  multiline = true,
}: HighlighterProps) => {
  const elementRef = useRef<HTMLSpanElement>(null)
  const isInView = useInView(elementRef, { once: true, margin: '-10%' })

  useLayoutEffect(() => {
    const element = elementRef.current
    if (!isInView || !element) return

    const annotation: RoughAnnotation = annotate(element, {
      type: action,
      color,
      strokeWidth,
      animationDuration,
      iterations,
      padding,
      multiline,
    })
    annotation.show()

    let lastWidth = element.offsetWidth
    let lastHeight = element.offsetHeight
    const resizeObserver = new ResizeObserver(() => {
      if (
        element.offsetWidth === lastWidth &&
        element.offsetHeight === lastHeight
      ) {
        return
      }
      lastWidth = element.offsetWidth
      lastHeight = element.offsetHeight
      annotation.hide()
      annotation.show()
    })
    resizeObserver.observe(element)

    return () => {
      annotation.remove()
      resizeObserver.disconnect()
    }
  }, [
    isInView,
    action,
    color,
    strokeWidth,
    animationDuration,
    iterations,
    padding,
    multiline,
  ])

  return (
    <span ref={elementRef} className="relative inline-block bg-transparent">
      {children}
    </span>
  )
}
